import { useState } from "react";
import { closeModal as closeOasisModal, Modal } from "risa-oasis-ui_v2";

import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import { ModalId } from "../../../enums/modalId";
import CallSummaryPane from "./panes/callSummaryPane";
import CaseDetailsPane from "./panes/caseDetailsPane";
import { CallSummaryMode } from "./utils/callSummaryMode";

interface NonEpaModalProps {
  data: PharmaStpFileModel;
  onClose: () => void;
  onSuccess?: () => void;
  /**
   * Fixed Second STP Outcome value. Used from Case Appeals so the field shows
   * "Appeals" (disabled) instead of mirroring the Call/Fax tab.
   */
  secondStpOutcomeLabel?: string;
}

/**
 * Dialog height as a percentage of the viewport. The Fax tab is taller because
 * of the extra Attach Document row.
 */

/**
 * Two-pane modal opened from the Add/Edit button of the calling worklist table.
 * Left pane: read-only case details and the calling history for the order.
 * Right pane: the Call / Fax summary form with its own action footer.
 *
 * @param props.data - Order row the modal was opened for
 * @param props.onClose - Clears the opened modal in redux
 * @param props.onSuccess - Called after a successful save so the table refetches
 * @param props.secondStpOutcomeLabel - Optional fixed Second STP Outcome label
 */
const NonEpaModal = (props: NonEpaModalProps) => {
  const [mode, setMode] = useState<CallSummaryMode>(CallSummaryMode.CALL);
  const title = props.data?.covermymed_id ? "Edit Summary" : "Add Summary";

  const handleClose = () => {
    closeOasisModal(ModalId.NON_EPA_MODAL);
    props.onClose();
  };

  return (
    <Modal
      dialogId={ModalId.NON_EPA_MODAL}
      title={title}
      onClose={props.onClose}
      onCancel={handleClose}
      onSave={handleClose}
      saveButtonText="Save"
      cancelText="Cancel"
      hideFooter
      heightPercentage={88}
    >
      {/* `w-screen max-w-full` widens the dialog to the 70% viewport cap set by
          the shared `dialog.custom-modal.show` rule without overflowing it. */}
      <div className="flex h-full w-screen max-w-full py-3">
        <div className="flex h-full min-h-0 w-full overflow-hidden rounded-lg border border-primaryGray-15">
          <div className="w-2/5 min-w-0 shrink-0 border-r border-primaryGray-15">
            <CaseDetailsPane data={props.data} />
          </div>
          <div className="min-w-0 flex-1">
            <CallSummaryPane
              data={props.data}
              mode={mode}
              onModeChange={setMode}
              onClose={handleClose}
              onSuccess={props.onSuccess}
              secondStpOutcomeLabel={props.secondStpOutcomeLabel}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default NonEpaModal;
